// - - - - - - - - - -
// #168 — pure helpers for the Next Day Openings dashboard concurrency
// throttle (see useDashboardOpenings.js).
//
// Firing 3 queries × N locations at once (153 for "all regions") storms API
// Gateway; getProjectsByCompanyWithShared then errors internally and returns
// its silent { projects: [], meta:{error} } sentinel, inflating the openings
// count with phantom installers. The hook bounds the in-flight set to a fixed
// number of locations and releases more as earlier ones settle.
//
// The two decisions below are extracted as pure functions so they can be
// unit-tested directly (the hook itself needs a QueryClient + rendered
// component to exercise). Test/production parity is guaranteed because the
// hook imports these exact functions.

/**
 * Count how many locations have FULLY settled — all 3 of their queries are
 * either success or error. A disabled (not-yet-released) query is pending/idle
 * (neither success nor error), so it is correctly NOT counted as settled; that
 * is what makes the release window advance only on real completion rather than
 * immediately releasing everything.
 *
 * @param {Array<{isSuccess?: boolean, isError?: boolean}>} results - flat
 *   useQueries results, 3 per location in order (candidates, attributes, projects)
 * @param {number} totalLocations
 * @returns {number}
 */
export function countSettledLocations(results, totalLocations) {
  if (!Array.isArray(results)) return 0;
  const settled = (r) => !!r && (r.isSuccess === true || r.isError === true);
  let done = 0;
  for (let i = 0; i < totalLocations; i++) {
    if (
      settled(results[i * 3 + 0]) &&
      settled(results[i * 3 + 1]) &&
      settled(results[i * 3 + 2])
    ) {
      done++;
    }
  }
  return done;
}

/**
 * Compute the next release window size.
 *
 *   - When the selected-location SET changes (`keyChanged`), reset to
 *     `concurrency` so a brand-new fan-out starts bounded.
 *   - Otherwise grow the window to `completedLocationCount + concurrency`
 *     (capped at `totalLocations`), but NEVER shrink — the window is
 *     monotonic between resets so the math converges instead of thrashing.
 *
 * @param {Object} p
 * @param {boolean} p.keyChanged
 * @param {number} p.prevReleased
 * @param {number} p.completedLocationCount
 * @param {number} p.totalLocations
 * @param {number} p.concurrency
 * @returns {number}
 */
export function computeNextReleased({
  keyChanged,
  prevReleased,
  completedLocationCount,
  totalLocations,
  concurrency,
}) {
  if (keyChanged) return Math.min(concurrency, totalLocations || concurrency);
  const target = Math.min(
    totalLocations,
    completedLocationCount + concurrency
  );
  return target > prevReleased ? target : prevReleased;
}
